/**
 * Live chat notice helpers — public vs private DM toasts.
 * Keep isNew off the React setState updater so toasts always fire.
 */

import type { ChatMessage } from "./liveCollaborationTypes";

const CHAT_ID_CAP = 500;

export function chatNoticeText(
  message: Pick<ChatMessage, "channel" | "sender" | "body">,
): string {
  const from = message.sender?.display_name || "Someone";
  const body = String(message.body || "").trim();
  const short = body.length > 80 ? `${body.slice(0, 77)}…` : body;
  const kind = message.channel === "dm" ? "Private message" : "New message";
  return short ? `${kind} from ${from}: ${short}` : `${kind} from ${from}`;
}

export function chatNoticePeerId(
  message: Pick<ChatMessage, "channel" | "sender">,
): string | undefined {
  if (message.channel !== "dm") return undefined;
  const peer = String(message.sender?.identity || "").trim();
  return peer || undefined;
}

export function rememberChatId(
  set: Set<string>,
  id: string,
  cap = CHAT_ID_CAP,
): void {
  if (!id) return;
  set.add(id);
  if (set.size <= cap) return;
  const overflow = set.size - cap;
  let dropped = 0;
  for (const key of set) {
    set.delete(key);
    dropped += 1;
    if (dropped >= overflow) break;
  }
}

export function shouldNotifyIncomingChat(input: {
  localIdentity?: string;
  message: Pick<ChatMessage, "message_id" | "sender">;
  knownIds: Set<string>;
}): { notify: boolean; messageId: string } {
  const messageId = String(input.message.message_id || "").trim();
  if (!messageId) return { notify: false, messageId: "" };
  if (input.knownIds.has(messageId)) {
    return { notify: false, messageId };
  }
  const localId = String(input.localIdentity || "").trim();
  const senderId = String(input.message.sender?.identity || "").trim();
  if (localId && senderId && senderId === localId) {
    return { notify: false, messageId };
  }
  return { notify: true, messageId };
}
